import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS, ROUTE_PATHS, TOP_INDENTS, VALIDATION_RULES } from '@constants';
import { ACCOUNT_USER_ROLE } from '@enums';
import { useStore } from '@hooks';
import { Icon, Select } from '@ui';
import { Form } from 'antd';
import { observer } from 'mobx-react';
import { ACCOUNT_USER_ROLE_LABELS } from '../../../../../Profile/components/AccountSettings/components/TeamManagement/constants';
import {
    ButtonGroup,
    CancelButton,
    DividerLine,
    FormContainer,
    InputField,
    SendInviteButton,
    StyledCloseButton,
    StyledModal,
    StyledTypographyHeader
} from './styled';

export const InviteDeveloperModal = observer(
    ({ isModalOpen, setIsModalOpen, customTop, customLeft, closeWithoutNavigate = false }) => {
        const store = useStore();
        const { handleTeamMemberCreate } = store.accountsStore;
        const { setInviteDeveloper } = store.authStore;
        const navigate = useNavigate();
        const [form] = Form.useForm();
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            if (isModalOpen) {
                form.setFieldsValue({
                    role: ACCOUNT_USER_ROLE.DEVELOPER
                });
            }
        }, [isModalOpen, form]);

        const handleSubmit = async (values) => {
            setLoading(true);
            try {
                await handleTeamMemberCreate(
                    {
                        email: values.email,
                        role: ACCOUNT_USER_ROLE.DEVELOPER
                    },
                    () => {
                        form.resetFields();
                        setIsModalOpen(false);
                        if (!closeWithoutNavigate) {
                            navigate(ROUTE_PATHS.HOME);
                        }
                    }
                );
            } finally {
                setLoading(false);
            }
        };

        const handleCancel = () => {
            setIsModalOpen(false);
            if (!closeWithoutNavigate) {
                setInviteDeveloper(false);
                navigate(ROUTE_PATHS.HOME);
            }
        };

        return (
            <StyledModal
                width='630px'
                height='341px'
                open={isModalOpen}
                onCancel={handleCancel}
                footer={null}
                maskClosable={false}
                destroyOnClose
                $customTop={customTop}
                $customLeft={customLeft}
                closeIcon={
                    <StyledCloseButton $closeWithoutNavigate={closeWithoutNavigate}>
                        <Icon
                            component={() => <Icon.CloseIcon />}
                            size='24px'
                            color={closeWithoutNavigate ? COLORS.$gray60Black : undefined}
                        />
                    </StyledCloseButton>
                }
            >
                <Fragment>
                    <StyledTypographyHeader data-e2e='invite-developer_title'>
                        Invite a developer
                    </StyledTypographyHeader>
                </Fragment>
                <DividerLine style={TOP_INDENTS.M} />
                <Form
                    style={TOP_INDENTS.XL_PLUS}
                    form={form}
                    onFinish={handleSubmit}
                    layout='vertical'
                    validateTrigger={['onSubmit']}
                >
                    <FormContainer>
                        <Form.Item
                            name='email'
                            rules={[...VALIDATION_RULES.REQUIRED, ...VALIDATION_RULES.EMAIL]}
                            normalize={(value) => value?.trim()}
                        >
                            <InputField
                                type='email'
                                placeholder='Email address'
                                data-e2e='invite-developer_email_input'
                            />
                        </Form.Item>

                        <Form.Item name='role'>
                            <Select
                                disabled
                                label='Select Access'
                                options={[
                                    {
                                        value: ACCOUNT_USER_ROLE.DEVELOPER,
                                        label: ACCOUNT_USER_ROLE_LABELS[ACCOUNT_USER_ROLE.DEVELOPER].title
                                    }
                                ]}
                            />
                        </Form.Item>

                        <ButtonGroup style={TOP_INDENTS.L}>
                            <CancelButton
                                data-e2e='invite-developer_cancel_btn'
                                onClick={() => handleCancel()}
                                type='default'
                                key='cancel'
                                disabled={loading}
                            >
                                Cancel
                            </CancelButton>
                            <Form.Item style={{ margin: 0 }}>
                                <SendInviteButton
                                    htmlType='submit'
                                    type='primary'
                                    data-e2e='invite-developer_submit_btn'
                                    key='submit'
                                    loading={loading}
                                    disabled={loading}
                                >
                                    Send Invite
                                </SendInviteButton>
                            </Form.Item>
                        </ButtonGroup>
                    </FormContainer>
                </Form>
            </StyledModal>
        );
    }
);

export default InviteDeveloperModal;
